import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.login(email, password);

      if (response.success && response.token) {
        // Store token and agent info
        localStorage.setItem('feedback_token', response.token);
        localStorage.setItem('feedback_agent', JSON.stringify(response.agent));

        // Full reload (not SPA navigate) so App re-reads the token from localStorage
        // and renders the authenticated shell. A client-side navigate('/') leaves
        // App's `isAuthenticated` state false, so the catch-all bounces back to /login.
        window.location.href = '/';
      } else {
        setError(response.error?.message || 'Login failed');
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Invalid email or password');
      } else {
        setError(err.message || 'An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-secondary)',
      padding: '16px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        padding: '40px 32px',
        background: 'var(--bg-primary)',
        borderRadius: 16,
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Brand */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 32,
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}>
          <img src="/coros-hex.svg" alt="COROS" width={34} height={38} style={{ display: 'block' }} />
          <div>
            <h1 style={{
              fontSize: 20,
              fontWeight: 900,
              color: 'var(--text-primary)',
              margin: '0 0 3px 0',
              lineHeight: 1,
            }}>
              Feedback Intelligence
            </h1>
            <p style={{
              fontFamily: 'var(--font-display)',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'var(--coros-red)',
              margin: 0,
              lineHeight: 1,
            }}>
              RELAY · COROS
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: 6,
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: 14,
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--coros-red)';
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: 6,
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: 14,
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--coros-red)';
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }}
            />
          </div>

          {error && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--color-error)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '11px 16px',
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              background: loading ? 'var(--text-muted)' : 'var(--coros-red)',
              border: 'none',
              borderRadius: 8,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: 'background 0.15s, opacity 0.15s',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.opacity = '1';
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
